import React, { useEffect, useRef, useState } from 'react';
import ChatMessage from '../ChatMessage/ChatMessage';
import PromptArea from '../PromptArea/PromptArea';
import { Typography, Spin, Button, Layout } from 'antd';
import { useChatState } from '../state/chatState';
import { useRecoilValue } from 'recoil';
import { myState } from 'recoil/atoms';
import { ArrowDownOutlined } from '@ant-design/icons';
import { Element } from 'react-scroll';
import { Mode } from '../state/chatState';

const Conversations: React.FC<{ mode: Mode }> = ({ mode }) => {
  const {
    state: { messages, isLoading, intermediateMessages, isDocumentMode },
    scrollToBottom
  } = useChatState();
  const me = useRecoilValue(myState);

  const [showScrollButton, setShowScrollButton] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const checkIfUserNearBottom = () => {
    const container = containerRef.current;
    if (!container) return true;
    const threshold = 200;
    const position = container.scrollHeight - container.scrollTop - container.clientHeight;

    return position < threshold;
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      // Add scroll listener to show/hide button when user scrolls
      const handleScroll = () => {
        const nearBottom = checkIfUserNearBottom();
        setShowScrollButton(!nearBottom);
      };

      container.addEventListener('scroll', handleScroll);

      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isLoading.isMessageLoading]);
  const modeHeight = {
    standalone: isDocumentMode ? 'h-[calc(100vh-380px)]' : 'h-[calc(100vh-345px)]',
    embedded: 'h-[calc(100vh-60px)]',
    horizontal: isDocumentMode ? 'h-[calc(60vh-124px)]' : 'h-[calc(60vh-60px)]'
  };

  if (isLoading.isMessageLoading) {
    return (
      <div data-testid="loading-container" className={`flex justify-center items-center ${modeHeight[mode]}`}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={`flex flex-col  ${modeHeight[mode]}`}>
      {isLoading.isMessageLoading && isLoading.isSessionLoading && (
        <div data-testid="loading-container" className={`flex justify-center items-center ${modeHeight[mode]}`}>
          <Spin size="large" />
        </div>
      )}
      <div ref={containerRef} id="ContainerElementID" className={`flex-1 overflow-y-auto relative ${modeHeight[mode]}`}>
        {messages.length === 0 ? (
          <div data-testid="greeting-container" className={`flex flex-col items-start justify-end h-full pb-4`}>
            <Typography.Title level={4} className="!mb-0">
              {(() => {
                const hours = new Date().getHours();
                let greeting = 'Good Evening';

                if (hours < 12) {
                  greeting = 'Good Morning';
                } else if (hours < 17) {
                  greeting = 'Good Afternoon';
                }

                return `${greeting} ${me?.name || ''}!`;
              })()}
            </Typography.Title>
            <Typography.Text>How can I help you today....</Typography.Text>
          </div>
        ) : (
          <div data-testid="messages-container">
            {messages.map((msg, index) => (
              <ChatMessage
                key={msg.id || index}
                message={msg}
                isLatestAssistantMessage={msg.type === 'ai' && index === messages.length - 1}
                isStreaming={isLoading.isStreaming && msg.type === 'assistantResponse' && index === messages.length - 1}
                files={msg.metadata?.files?.length ? msg.metadata?.files : undefined}
              />
            ))}
            {intermediateMessages && (
              <ChatMessage
                key={intermediateMessages.id}
                message={intermediateMessages}
                isLatestAssistantMessage={false}
                isStreaming={isLoading.isStreaming}
                files={intermediateMessages.metadata?.files?.length ? intermediateMessages.metadata?.files : undefined}
              />
            )}
            {isLoading.isStreaming && <div data-testid="loader" className="loader"></div>}
            <Element name="bottom" />
          </div>
        )}
      </div>
      {showScrollButton && (
        <Button
          type="primary"
          icon={<ArrowDownOutlined />}
          onClick={() => scrollToBottom()}
          className="!bg-gray-800/80 !hover:bg-gray-800/100 !rounded-full !w-10 !h-10 relative bottom-[10px] self-center filter drop-shadow-lg cursor-pointer"
        />
      )}
      <PromptArea mode={mode} />
    </div>
  );
};

export default Conversations;
